import React, { useState, useEffect, useMemo } from 'react';
import { faqAdminAPI } from '../../services/api';

const FaqEditor = () => {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: '',
    keywords: '',
    related_link: '',
    display_order: 0,
    is_active: true,
  });

  const relatedLinks = [
    { value: '', label: 'None' },
    { value: '/apps/portfolio-blender', label: 'Portfolio Blender' },
    { value: '/apps/research-reports', label: 'Research Reports' },
    { value: '/apps/fund-screener', label: 'Fund Screener' },
    { value: '/apps/portfolio-viewer', label: 'Portfolio Viewer' },
    { value: '/apps/portfolio-constructor', label: 'Portfolio Constructor' },
    { value: '/apps/resources', label: 'Resources' },
    { value: '/forgot-password', label: 'Forgot Password' },
    { value: '/dashboard', label: 'Dashboard' },
    { value: '/faq', label: 'FAQ' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [faqData, categoryData] = await Promise.all([
        faqAdminAPI.getAll(),
        faqAdminAPI.getCategories(),
      ]);
      setFaqs(faqData);
      setCategories(categoryData);

      // Set default category if available
      if (categoryData.length > 0 && !formData.category) {
        setFormData(prev => ({ ...prev, category: categoryData[0].name }));
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load data' });
    } finally {
      setIsLoading(false);
    }
  };

  // Get category names for dropdown
  const categoryNames = useMemo(() => {
    return categories.filter(c => c.is_active).map(c => c.name);
  }, [categories]);

  // Count FAQs per category
  const faqCountByCategory = useMemo(() => {
    return faqs.reduce((acc, faq) => {
      acc[faq.category] = (acc[faq.category] || 0) + 1;
      return acc;
    }, {});
  }, [faqs]);

  const resetForm = () => {
    setFormData({
      question: '',
      answer: '',
      category: categoryNames[0] || '',
      keywords: '',
      related_link: '',
      display_order: 0,
      is_active: true,
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    try {
      if (editingId) {
        await faqAdminAPI.update(editingId, formData);
        setMessage({ type: 'success', text: 'FAQ updated successfully' });
      } else {
        await faqAdminAPI.create(formData);
        setMessage({ type: 'success', text: 'FAQ created successfully' });
      }
      resetForm();
      fetchData();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to save FAQ',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (faq) => {
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      keywords: faq.keywords || '',
      related_link: faq.related_link || '',
      display_order: faq.display_order,
      is_active: faq.is_active,
    });
    setEditingId(faq.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;

    setIsLoading(true);
    try {
      await faqAdminAPI.delete(id);
      setMessage({ type: 'success', text: 'FAQ deleted successfully' });
      fetchData();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to delete FAQ',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async (faq) => {
    try {
      await faqAdminAPI.update(faq.id, { is_active: !faq.is_active });
      fetchData();
    } catch (err) {
      setMessage({
        type: 'error',
        text: 'Failed to update FAQ status',
      });
    }
  };

  // Category management
  const handleAddCategory = async () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;

    try {
      await faqAdminAPI.createCategory({
        name: trimmed,
        display_order: categories.length,
      });
      setNewCategoryName('');
      setMessage({ type: 'success', text: `Category "${trimmed}" added` });
      fetchData();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to add category',
      });
    }
  };

  const handleDeleteCategory = async (category) => {
    const count = faqCountByCategory[category.name] || 0;
    if (count > 0) {
      setMessage({
        type: 'error',
        text: `Cannot delete category with ${count} FAQ(s). Move or delete FAQs first.`,
      });
      return;
    }

    if (!confirm(`Delete category "${category.name}"?`)) return;

    try {
      await faqAdminAPI.deleteCategory(category.id);
      setMessage({ type: 'success', text: `Category "${category.name}" deleted` });
      fetchData();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to delete category',
      });
    }
  };

  // Group FAQs by category for display
  const groupedFaqs = faqs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {});

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">FAQ Management</h5>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setShowCategoryManager(!showCategoryManager)}
          >
            {showCategoryManager ? 'Hide Categories' : 'Manage Categories'}
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              resetForm();
              setShowForm(!showForm);
            }}
          >
            {showForm ? 'Cancel' : 'New FAQ'}
          </button>
        </div>
      </div>
      <div className="card-body">
        {message && (
          <div
            className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} alert-dismissible`}
          >
            {message.text}
            <button
              type="button"
              className="btn-close"
              onClick={() => setMessage(null)}
            ></button>
          </div>
        )}

        {/* Category Manager */}
        {showCategoryManager && (
          <div className="card mb-4 border-secondary">
            <div className="card-header bg-light">
              <h6 className="mb-0">Manage Categories</h6>
            </div>
            <div className="card-body">
              {/* Add new category */}
              <div className="row mb-3">
                <div className="col-md-8">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="newCategoryName"
                      name="newCategoryName"
                      placeholder="New category name..."
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={handleAddCategory}
                      disabled={!newCategoryName.trim()}
                    >
                      Add Category
                    </button>
                  </div>
                </div>
              </div>

              {/* List of categories */}
              <div>
                <h6 className="text-muted mb-2">Categories</h6>
                {categories.length === 0 ? (
                  <p className="text-muted small">No categories yet. Add your first category above.</p>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {categories.map(cat => {
                      const count = faqCountByCategory[cat.name] || 0;
                      const canDelete = count === 0;
                      return (
                        <span
                          key={cat.id}
                          className={`badge ${cat.is_active ? 'bg-primary' : 'bg-secondary'} d-flex align-items-center gap-1`}
                        >
                          {cat.name} ({count})
                          {canDelete && (
                            <button
                              type="button"
                              className="btn-close btn-close-white ms-1"
                              style={{ fontSize: '0.6rem' }}
                              onClick={() => handleDeleteCategory(cat)}
                              title="Delete category"
                            ></button>
                          )}
                        </span>
                      );
                    })}
                  </div>
                )}
                <small className="text-muted d-block mt-2">
                  Categories with FAQs cannot be deleted. Move or delete FAQs first.
                </small>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Form */}
        {showForm && (
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">{editingId ? 'Edit FAQ' : 'Create New FAQ'}</h6>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label" htmlFor="faqQuestion">Question *</label>
                  <input
                    type="text"
                    className="form-control"
                    id="faqQuestion"
                    name="question"
                    value={formData.question}
                    onChange={(e) =>
                      setFormData({ ...formData, question: e.target.value })
                    }
                    required
                    placeholder="e.g., How do I reset my password?"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label" htmlFor="faqAnswer">Answer *</label>
                  <textarea
                    className="form-control"
                    id="faqAnswer"
                    name="answer"
                    rows={6}
                    value={formData.answer}
                    onChange={(e) =>
                      setFormData({ ...formData, answer: e.target.value })
                    }
                    required
                    placeholder="Provide a clear and helpful answer..."
                  />
                  <small className="text-muted">
                    Supports basic formatting. Use line breaks for paragraphs.
                  </small>
                </div>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label" htmlFor="faqCategory">Category *</label>
                    <select
                      className="form-select"
                      id="faqCategory"
                      name="category"
                      value={formData.category}
                      onChange={(e) =>
                        setFormData({ ...formData, category: e.target.value })
                      }
                      required
                    >
                      <option value="">Select category...</option>
                      {categoryNames.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={() => setShowCategoryManager(true)}
                      >
                        + Add new category
                      </button>
                    </small>
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label" htmlFor="faqRelatedLink">Related Link</label>
                    <select
                      className="form-select"
                      id="faqRelatedLink"
                      name="related_link"
                      value={formData.related_link}
                      onChange={(e) =>
                        setFormData({ ...formData, related_link: e.target.value })
                      }
                    >
                      {relatedLinks.map((link) => (
                        <option key={link.value} value={link.value}>
                          {link.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label" htmlFor="faqDisplayOrder">Display Order</label>
                    <input
                      type="number"
                      className="form-control"
                      id="faqDisplayOrder"
                      name="display_order"
                      value={formData.display_order}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          display_order: parseInt(e.target.value) || 0,
                        })
                      }
                      min="0"
                    />
                    <small className="text-muted">Lower numbers appear first</small>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label" htmlFor="faqKeywords">Keywords</label>
                  <input
                    type="text"
                    className="form-control"
                    id="faqKeywords"
                    name="keywords"
                    value={formData.keywords}
                    onChange={(e) =>
                      setFormData({ ...formData, keywords: e.target.value })
                    }
                    placeholder="e.g., password, login, reset, forgot (comma-separated)"
                  />
                  <small className="text-muted">
                    Helps with search - separate with commas
                  </small>
                </div>

                <div className="mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="isActive"
                      checked={formData.is_active}
                      onChange={(e) =>
                        setFormData({ ...formData, is_active: e.target.checked })
                      }
                    />
                    <label className="form-check-label" htmlFor="isActive">
                      Active (visible to users)
                    </label>
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading || !formData.category}
                  >
                    {isLoading ? 'Saving...' : editingId ? 'Update FAQ' : 'Create FAQ'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* FAQ List */}
        {isLoading && !showForm ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : faqs.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <p>No FAQs yet. Create your first FAQ to get started.</p>
          </div>
        ) : (
          <div>
            {/* Stats */}
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card bg-light">
                  <div className="card-body text-center py-2">
                    <h4 className="mb-0">{faqs.length}</h4>
                    <small className="text-muted">Total FAQs</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-light">
                  <div className="card-body text-center py-2">
                    <h4 className="mb-0">{faqs.filter(f => f.is_active).length}</h4>
                    <small className="text-muted">Active</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-light">
                  <div className="card-body text-center py-2">
                    <h4 className="mb-0">{Object.keys(groupedFaqs).length}</h4>
                    <small className="text-muted">Categories Used</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-light">
                  <div className="card-body text-center py-2">
                    <h4 className="mb-0">{faqs.filter(f => f.related_link).length}</h4>
                    <small className="text-muted">With Links</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Grouped FAQ List */}
            {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
              <div key={category} className="mb-4">
                <h6 className="text-primary border-bottom pb-2 mb-3">
                  {category}
                  <span className="badge bg-secondary ms-2">{categoryFaqs.length}</span>
                </h6>
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th style={{ width: '40%' }}>Question</th>
                        <th style={{ width: '30%' }}>Answer Preview</th>
                        <th style={{ width: '10%' }}>Status</th>
                        <th style={{ width: '10%' }}>Order</th>
                        <th style={{ width: '10%' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryFaqs
                        .sort((a, b) => a.display_order - b.display_order)
                        .map((faq) => (
                          <tr key={faq.id} className={!faq.is_active ? 'table-secondary' : ''}>
                            <td>
                              <div className="fw-medium">{faq.question}</div>
                              {faq.keywords && (
                                <small className="text-muted">
                                  Keywords: {faq.keywords}
                                </small>
                              )}
                            </td>
                            <td>
                              <small className="text-muted">
                                {faq.answer.length > 100
                                  ? faq.answer.substring(0, 100) + '...'
                                  : faq.answer}
                              </small>
                              {faq.related_link && (
                                <div>
                                  <span className="badge bg-info">
                                    Link: {faq.related_link}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td>
                              <button
                                className={`btn btn-sm ${faq.is_active ? 'btn-success' : 'btn-secondary'}`}
                                onClick={() => handleToggleActive(faq)}
                                title={faq.is_active ? 'Click to deactivate' : 'Click to activate'}
                              >
                                {faq.is_active ? 'Active' : 'Inactive'}
                              </button>
                            </td>
                            <td>{faq.display_order}</td>
                            <td>
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-outline-primary"
                                  onClick={() => handleEdit(faq)}
                                  title="Edit"
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn btn-outline-danger"
                                  onClick={() => handleDelete(faq.id)}
                                  title="Delete"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FaqEditor;
